import React, { useState, useEffect } from 'react';
import { extractTailwindClasses } from '../../../utils/visualEditor/elementParser';

/**
 * Typography Editor
 * Edit font size, weight, family, alignment
 */
export default function TypographyEditor({ element, onUpdate }) {
  const [fontSize, setFontSize] = useState('text-base');
  const [fontWeight, setFontWeight] = useState('font-normal');
  const [textAlign, setTextAlign] = useState('text-left');
  const [fontFamily, setFontFamily] = useState('font-sans');

  useEffect(() => {
    if (element) {
      const twClasses = extractTailwindClasses(element.element);
      setFontSize(twClasses.typography.fontSize || 'text-base');
      setFontWeight(twClasses.typography.fontWeight || 'font-normal');
      setTextAlign(twClasses.typography.textAlign || 'text-left');
      
      // Extract font family from classList
      const fontFamilyClass = Array.from(element.element.classList).find(c => 
        c.match(/^font-(sans|serif|mono)$/)
      );
      setFontFamily(fontFamilyClass || 'font-sans');
    }
  }, [element]);

  const handleFontSizeChange = (e) => {
    const newSize = e.target.value;
    setFontSize(newSize);
    updateClasses('fontSize', newSize);
  };

  const handleFontWeightChange = (e) => {
    const newWeight = e.target.value;
    setFontWeight(newWeight);
    updateClasses('fontWeight', newWeight);
  };

  const handleTextAlignChange = (alignment) => {
    setTextAlign(alignment);
    updateClasses('textAlign', alignment);
  };

  const handleFontFamilyChange = (e) => {
    const newFamily = e.target.value;
    setFontFamily(newFamily);
    updateClasses('fontFamily', newFamily);
  };

  const updateClasses = (property, value) => {
    if (!element) return;
    
    // Determine the style path
    // If element has data-style-path, use that
    // Otherwise, infer from data-path
    const stylePath = element.element.getAttribute('data-style-path') || 
                     inferStylePath(element.dataPath);
    
    if (!stylePath) {
      console.warn('No style path found for element');
      return;
    }
    
    // Update the specific style property
    const fullPath = `${stylePath}.${getStyleProperty(property)}`;
    console.log('Updating style:', fullPath, '→', value);
    onUpdate(fullPath, value);
  };

  const inferStylePath = (dataPath) => {
    if (!dataPath) return null;
    
    // Try to map data path to style path
    // e.g., copy.headline → styles.headline
    const parts = dataPath.split('.');
    const lastPart = parts[parts.length - 1];
    
    // Remove array indices
    const cleanPart = lastPart.replace(/\[\d+\]/, '');
    
    return `styles.${cleanPart}`;
  };

  const getStyleProperty = (property) => {
    const map = {
      fontSize: 'fontSize',
      fontWeight: 'fontWeight',
      textAlign: 'textAlign',
      fontFamily: 'fontFamily'
    };
    return map[property] || property;
  };

  const fontSizes = [
    { value: 'text-xs', label: 'XS (12px)' },
    { value: 'text-sm', label: 'SM (14px)' },
    { value: 'text-base', label: 'Base (16px)' },
    { value: 'text-lg', label: 'LG (18px)' },
    { value: 'text-xl', label: 'XL (20px)' },
    { value: 'text-2xl', label: '2XL (24px)' },
    { value: 'text-3xl', label: '3XL (30px)' },
    { value: 'text-4xl', label: '4XL (36px)' },
    { value: 'text-5xl', label: '5XL (48px)' },
  ];

  const fontWeights = [
    { value: 'font-thin', label: 'Thin' },
    { value: 'font-light', label: 'Light' },
    { value: 'font-normal', label: 'Normal' },
    { value: 'font-medium', label: 'Medium' },
    { value: 'font-semibold', label: 'Semibold' },
    { value: 'font-bold', label: 'Bold' },
    { value: 'font-extrabold', label: 'Extra Bold' },
  ];

  const fontFamilies = [
    { value: 'font-sans', label: 'Sans Serif' },
    { value: 'font-serif', label: 'Serif' },
    { value: 'font-mono', label: 'Monospace' },
  ];

  return (
    <div className="space-y-4">
      {/* Font Size */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-2">
          Font Size
        </label>
        <select
          value={fontSize}
          onChange={handleFontSizeChange}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          {fontSizes.map(size => (
            <option key={size.value} value={size.value}>
              {size.label}
            </option>
          ))}
        </select>
      </div>

      {/* Font Weight */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-2">
          Font Weight
        </label>
        <select
          value={fontWeight}
          onChange={handleFontWeightChange}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          {fontWeights.map(weight => (
            <option key={weight.value} value={weight.value}>
              {weight.label}
            </option>
          ))}
        </select>
      </div>

      {/* Font Family */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-2">
          Font Family
        </label>
        <select
          value={fontFamily}
          onChange={handleFontFamilyChange}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          {fontFamilies.map(family => (
            <option key={family.value} value={family.value}>
              {family.label}
            </option>
          ))}
        </select>
      </div>

      {/* Text Alignment */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-2">
          Text Alignment
        </label>
        <div className="flex gap-2">
          {['text-left', 'text-center', 'text-right', 'text-justify'].map(align => (
            <button
              key={align}
              onClick={() => handleTextAlignChange(align)}
              className={`flex-1 px-3 py-2 text-xs border rounded-lg transition-colors ${
                textAlign === align
                  ? 'bg-blue-500 text-white border-blue-500'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              {align.replace('text-', '')}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

